import { Navigate, Outlet } from "react-router-dom";
import type { PermissionKey } from "@shared/types/auth";
import { useAuth, userCan } from "./AuthContext";

/**
 * Layout route for Nenda-staff pages: renders the page only for a session the
 * server would let in, and sends anyone else to the landing page.
 *
 * The menu already hides these pages, but the URL bar is a second way in. The
 * API refuses the data either way; this stops a customer from being shown the
 * page's shell - its headings, its empty tables, its "New …" buttons - for
 * a surface that was never theirs.
 *
 * - `permission` set: the same `userCan` the menu uses, which refuses a
 *   staff-only key to any tenant-scoped user.
 * - no `permission`: the page is gated on the admin role itself, as the menu's
 *   `adminOnly` items are.
 *
 * Keep each route's gate in step with its nav item in `RootLayout.tsx`.
 */
export default function RequireStaff({
  permission,
}: {
  permission?: PermissionKey;
}) {
  const { user } = useAuth();
  const allowed = permission
    ? userCan(user, permission)
    : user?.type === "admin";
  return allowed ? <Outlet /> : <Navigate to="/" replace />;
}
